import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClientAuth } from '../../contexts/ClientAuthContext';
import { Mail, Lock, ArrowRight, Loader2, User } from 'lucide-react';
import LanguageSelector from '../../components/LanguageSelector';
import { Turnstile } from '../../components/Turnstile';
import GoogleSignInButton from '../../components/GoogleSignInButton';
import { trackConversion } from '../../lib/analytics';

function useErrorMessage(code: string): string {
  const { t } = useTranslation();
  if (!code) return '';
  const key = `portal.register.errors.${code}`;
  const translated = t(key);
  return translated !== key ? translated : t('portal.register.errors.unknown');
}

// Minimal signup gate — email + password only. Everything else (business,
// properties, plan, etc.) is collected afterwards in the onboarding flow.
export default function ClientRegisterPage() {
  const { register } = useClientAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Carry the plan chosen on the landing page (?units=&billing=&sites=) into the
  // in-app payment step after registration.
  useEffect(() => {
    const units = searchParams.get('units');
    const billing = searchParams.get('billing');
    const sites = searchParams.get('sites');
    if (units || billing || sites) {
      try {
        localStorage.setItem('kx_plan', JSON.stringify({
          units: units ? Number(units) : undefined,
          billing: billing === 'yearly' ? 'yearly' : billing === 'monthly' ? 'monthly' : undefined,
          siteModel: sites === 'per_property' ? 'per_property' : sites === 'combined' ? 'combined' : undefined,
        }));
      } catch { /* ignore */ }
    }
  }, [searchParams]);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorCode, setErrorCode] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');

  const error = useErrorMessage(errorCode);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorCode('');
    setLoading(true);
    try {
      await register({ email, password, name: name.trim() || undefined, turnstileToken, lang: i18n.language });
      trackConversion('app_register_submit', {}, 'CompleteRegistration');
      navigate('/portal/dashboard'); // logged in → straight into onboarding
    } catch (err) {
      setErrorCode(err instanceof Error ? err.message : 'unknown');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative p-4" style={{ background: '#0f172a' }}>
      <picture className="absolute inset-0 w-full h-full">
        <source srcSet="/login-bg.avif" type="image/avif" />
        <img src="/login-bg.png" alt="" className="absolute inset-0 w-full h-full object-cover" loading="eager" />
      </picture>
      <div className="absolute inset-0 bg-slate-900/60" />
      <div className="absolute top-4 right-4 z-10"><LanguageSelector /></div>

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/kanarix-logo-white.png" alt="Kanarix" className="h-auto max-h-20 w-auto mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white">{t('portal.register.title')}</h1>
          <p className="text-slate-400 mt-1">{t('portal.register.subtitle')}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-rose-50 text-rose-700 text-sm border border-rose-200">
              {error}
              {errorCode === 'already_registered' && (
                <> <Link to="/portal/login" className="font-medium underline">{t('portal.register.goLogin')}</Link></>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('portal.register.nameLabel', 'Tu nombre')}</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
                <input type="text" required value={name} onChange={e => setName(e.target.value)}
                  placeholder={t('portal.register.namePlaceholder', 'Nombre y apellidos')} className={inputCls} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('portal.register.emailLabel')}</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
                <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                  placeholder={t('portal.register.emailPlaceholder')} className={inputCls} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('portal.register.passwordLabel')}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
                <input type="password" required minLength={8} value={password} onChange={e => setPassword(e.target.value)}
                  placeholder={t('portal.register.passwordPlaceholder')} className={inputCls} />
              </div>
              <p className="text-xs text-slate-400 mt-1">{t('portal.register.passwordHint')}</p>
            </div>

            <Turnstile onVerify={setTurnstileToken} />
            <button type="submit" disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white font-medium rounded-lg transition-colors">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
              {t('portal.register.submit')}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400">{t('portal.login.or', 'o')}</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
          <GoogleSignInButton label={t('portal.register.google', 'Registrarse con Google')} />

          <div className="mt-6 pt-5 border-t border-slate-100 text-center text-sm text-slate-500">
            {t('portal.register.haveAccount')}{' '}
            <Link to="/portal/login" className="text-teal-600 hover:text-teal-700 font-medium">
              {t('portal.register.login')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls = 'w-full pl-10 pr-3 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm';
